import { useState } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import useContactMerges from "../../data/useContactMerges";
import useWilmaDeletedUsers from "../../data/useWilmaDeletedUsers";
import { IContactMergeSummary } from "../../models/Contact";
import formatDateTime from "../../utils/formatDateTime";
import Card from "../shared/Card/Card";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";
import StatusPill, { StatusPillColour } from "../shared/StatusPill";
import { ContactUnmergeModal } from "./ContactUnmergeModal";

// The contacts merged into this one, newest first, each with an Unmerge. Admin only.
export function ContactMergeHistory({ contactId }: { contactId: string }) {
  const { t } = useTranslation("accounts");
  const { merges } = useContactMerges(contactId, true, "ContactMergeHistory");
  const { wilmaUsers } = useWilmaDeletedUsers();
  const [unmergeId, setUnmergeId] = useState<string | null>(null);

  const userName = (userId: string | null) => {
    if (!userId) return t("System");
    const user = wilmaUsers.data?.data?.find((u: any) => u.id === userId);
    return user ? `${user.forename} ${user.surname}` : t("Unknown");
  };

  const moved = (merge: IContactMergeSummary) =>
    [
      t("{{count}} lead(s)", { count: merge.leads }),
      merge.documents > 0 && t("{{count}} document(s)", { count: merge.documents }),
      merge.messages > 0 && t("{{count}} message(s)", { count: merge.messages }),
      merge.emails > 0 && t("{{count}} email(s)", { count: merge.emails }),
      merge.orders > 0 && t("{{count}} order(s)", { count: merge.orders }),
    ]
      .filter(Boolean)
      .join(", ");

  const rows = merges.data?.data ?? [];

  return (
    <Card title={t("Merge history")} subtitle={t("Contacts merged into this one. Unmerge brings one back with what it brought.")} bodyClassName="p-4">
      {merges.isLoading ? (
        <LoadingWheel />
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">{t("Nothing has been merged into this contact.")}</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {rows.map((merge) => (
            <li key={merge.mergeId} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0 text-sm">
                <p className="font-medium text-gray-900">
                  {merge.undoneAt ? (
                    <Link to={`/contacts/${merge.sourceContactId}`} className="text-primary-600 hover:underline">
                      {merge.sourceName || t("(no name)")}
                    </Link>
                  ) : (
                    merge.sourceName || t("(no name)")
                  )}
                  {merge.kind === "Tidy" && (
                    <span className="ml-2">
                      <StatusPill colour={StatusPillColour.Neutral} text={t("Tidy")} />
                    </span>
                  )}
                </p>
                <p className="text-gray-500">
                  {t("Merged {{date}} by {{user}} · {{moved}}", {
                    date: formatDateTime(merge.mergedAt, DateTime.DATETIME_SHORT),
                    user: userName(merge.mergedBy),
                    moved: moved(merge),
                  })}
                </p>
                {merge.undoneAt && (
                  <p className="text-gray-500">
                    {t("Unmerged {{date}} by {{user}}", {
                      date: formatDateTime(merge.undoneAt, DateTime.DATETIME_SHORT),
                      user: userName(merge.undoneBy),
                    })}
                  </p>
                )}
              </div>
              {!merge.undoneAt && (
                <ButtonInput label={t("Unmerge")} isSubmit={false} onClick={() => setUnmergeId(merge.mergeId)} classes="" size="sm" />
              )}
            </li>
          ))}
        </ul>
      )}

      <ContactUnmergeModal mergeId={unmergeId} setMergeId={setUnmergeId} />
    </Card>
  );
}
